<script setup lang="ts">
const columns = [{
  label: 'Product',
  children: [{
    label: 'About',
    to: '/about'
  }, {
    label: 'Features',
    to: '/features'
  }, {
    label: 'Help center',
    to: '/help'
  }, {
    label: 'Docs',
    to: '/docs/getting-started'
  }, {
    label: 'Suggestions',
    to: '/suggestions'
  }, {
    label: 'Blog',
    to: '/blog'
  }, {
    label: 'Skins',
    to: '/skins'
  }, {
    label: 'Pricing',
    to: '/pricing'
  }, {
    label: 'Leaderboard',
    to: '/leaderboard'
  }]
}, {
  label: 'Contact',
  children: [{
    label: 'support@mychar.gg',
    to: 'mailto:support@mychar.gg'
  }, {
    label: 'legal@mychar.gg',
    to: 'mailto:legal@mychar.gg'
  }, {
    label: 'info@mychar.gg',
    to: 'mailto:info@mychar.gg'
  }, {
    label: 'Discord',
    to: 'https://discord.gg/mychar',
    target: '_blank'
  }]
}, {
  label: 'Legal',
  children: [{
    label: 'Terms of Service',
    to: '/legal/terms'
  }, {
    label: 'Privacy Policy',
    to: '/legal/privacy'
  }, {
    label: 'Acceptable Use',
    to: '/legal/acceptable-use'
  }]
}]

const currentYear = computed(() => new Date().getFullYear())

// "Featured on" directory badges. Add entries as Turbo0 / findly / BetterLaunch
// approvals land (some require the badge on-site to lock the dofollow link).
// Commit badge images to public/badges/. Ships empty; the row hides itself.
const featuredBadges: { name: string, href: string, img: string }[] = [
  // { name: 'Turbo0', href: 'https://turbo0.com/item/mychar', img: '/badges/turbo0.svg' }
]
</script>

<template>
  <UFooter :ui="{ top: 'border-b border-default' }">
    <template #top>
      <UContainer>
        <UFooterColumns :columns="columns">
          <template #right>
            <div class="flex flex-col gap-4">
              <NuxtLink
                to="/"
                class="inline-flex"
              >
                <AppLogo />
              </NuxtLink>
              <p class="text-sm text-muted">
                The link in bio you design yourself.
              </p>
              <div class="text-xs text-muted space-y-0.5">
                <p>KVK: 93940254 | VAT: NL005053176B36</p>
                <p>Breda, The Netherlands</p>
              </div>

              <div
                v-if="featuredBadges.length"
                class="flex flex-col gap-2"
              >
                <p class="text-xs font-medium text-dimmed uppercase tracking-wide">
                  Featured on
                </p>
                <div class="flex flex-wrap items-center gap-3">
                  <NuxtLink
                    v-for="badge in featuredBadges"
                    :key="badge.name"
                    :to="badge.href"
                    target="_blank"
                    rel="noopener"
                    class="inline-flex"
                  >
                    <NuxtImg
                      :src="badge.img"
                      :alt="`mychar.gg featured on ${badge.name}`"
                      class="h-9 w-auto"
                      loading="lazy"
                    />
                  </NuxtLink>
                </div>
              </div>
            </div>
          </template>
        </UFooterColumns>
      </UContainer>
    </template>

    <template #left>
      <p class="text-muted text-sm">
        © {{ currentYear }} mychar.gg. All rights reserved.
      </p>
    </template>

    <template #right>
      <ColorModeToggle />
      <!-- Social icons in their brand colors (X and TikTok are monochrome, so they follow the color mode) -->
      <UTooltip text="X">
        <UButton
          to="https://x.com/mychargg"
          target="_blank"
          icon="i-simple-icons-x"
          aria-label="mychar.gg on X"
          color="neutral"
          variant="ghost"
          :ui="{ leadingIcon: 'text-highlighted' }"
        />
      </UTooltip>
      <UTooltip text="Instagram">
        <UButton
          to="https://www.instagram.com/mychargg/"
          target="_blank"
          icon="i-simple-icons-instagram"
          aria-label="mychar.gg on Instagram"
          color="neutral"
          variant="ghost"
          :ui="{ leadingIcon: 'bg-linear-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5]' }"
        />
      </UTooltip>
      <UTooltip text="TikTok">
        <UButton
          to="https://www.tiktok.com/@mychar.gg"
          target="_blank"
          icon="i-simple-icons-tiktok"
          aria-label="mychar.gg on TikTok"
          color="neutral"
          variant="ghost"
          :ui="{ leadingIcon: 'text-highlighted' }"
        />
      </UTooltip>
      <UTooltip text="YouTube">
        <UButton
          to="https://www.youtube.com/@mychargg"
          target="_blank"
          icon="i-simple-icons-youtube"
          aria-label="mychar.gg on YouTube"
          color="neutral"
          variant="ghost"
          :ui="{ leadingIcon: 'text-[#ff0000]' }"
        />
      </UTooltip>
      <UTooltip text="Discord">
        <UButton
          to="https://discord.gg/mychar"
          target="_blank"
          icon="i-simple-icons-discord"
          aria-label="mychar.gg on Discord"
          color="neutral"
          variant="ghost"
          :ui="{ leadingIcon: 'text-[#5865f2]' }"
        />
      </UTooltip>
    </template>
  </UFooter>
</template>
